import noop from "lodash/noop";

import { GroupClassRegistrationModalTestID } from "@/constants/TestID";
import useFeatureFlags from "@/hooks/useFeatureFlags";
import CoreTag from "@/ui/components/groupClasses/series/CoreTag";
import { Text } from "@/ui/core";
import { BaseCard, Box, HStack, VStack } from "@/ui/layout";
import { getIllustrationIcon } from "@/ui/theme/icons/illustrations/groupClasses";
import { ColorToken } from "@/ui/theme/tokens";
import { joinTestIDs, pluralize } from "@/utils/string.util";

interface GroupRegistrationCardHeaderProps extends ViewWithTestID {
  durationInWeeks: number;
  // v2 (redesign): show a "Core" tag when the group is core, nothing otherwise
  hasCoreTag?: boolean;
  iconName: string;
  title: string;
}

// redesign on: show the Core tag for core groups, and nothing for non-core groups.
// redesign off (v1): show the duration ("N weeks") badge.
const GroupCardBadge = ({
  durationInWeeks,
  hasCoreTag,
  testID,
}: {
  durationInWeeks: number;
  hasCoreTag?: boolean;
  testID: string;
}) => {
  const { isGroupClassRedesignEnabled } = useFeatureFlags();

  if (isGroupClassRedesignEnabled) {
    if (!hasCoreTag) return null;
    return (
      <Box flexShrink={0}>
        <CoreTag
          testID={joinTestIDs(
            testID,
            GroupClassRegistrationModalTestID.groupCardCoreTag,
          )}
        />
      </Box>
    );
  }

  return (
    <Box
      borderColor={ColorToken.NeutralLine}
      borderRadius={8}
      borderWidth={1}
      flexShrink={0}
      paddingHorizontal={8}
      paddingVertical={4}
      testID={joinTestIDs(
        testID,
        GroupClassRegistrationModalTestID.groupCardDurationBadge,
      )}
    >
      <Text
        testID={joinTestIDs(
          testID,
          GroupClassRegistrationModalTestID.groupCardDurationText,
        )}
        variant="captionMedium"
      >{`${durationInWeeks} week${pluralize({ itemCount: durationInWeeks })}`}</Text>
    </Box>
  );
};

const GroupRegistrationCardHeader = ({
  durationInWeeks,
  hasCoreTag,
  iconName,
  testID,
  title,
}: GroupRegistrationCardHeaderProps) => {
  const IllustrationIcon = getIllustrationIcon({ iconName });

  return (
    <HStack alignItems="center" gap={6} justifyContent="space-between">
      <HStack alignItems="center" flex={1} flexShrink={1} gap={12}>
        {IllustrationIcon}
        <Box flex={1} minWidth={0}>
          <Text
            flexShrink={1}
            testID={joinTestIDs(
              testID,
              GroupClassRegistrationModalTestID.groupCardTitle,
            )}
            variant="bodyMedium"
          >
            {title}
          </Text>
        </Box>
      </HStack>
      <GroupCardBadge
        durationInWeeks={durationInWeeks}
        hasCoreTag={hasCoreTag}
        testID={testID}
      />
    </HStack>
  );
};

interface GroupRegistrationCardProps extends ViewWithTestID {
  description: string;
  durationInWeeks: number;
  hasCoreTag?: boolean;
  iconName: string;
  title: string;
}

const GroupRegistrationCard = ({
  description,
  durationInWeeks,
  hasCoreTag,
  iconName,
  testID,
  title,
}: GroupRegistrationCardProps) => (
  <BaseCard
    isDisabled
    onPress={noop}
    testID={joinTestIDs(testID, GroupClassRegistrationModalTestID.groupCard)}
  >
    <VStack
      gap={6}
      testID={joinTestIDs(
        testID,
        GroupClassRegistrationModalTestID.groupCardContent,
      )}
    >
      <GroupRegistrationCardHeader
        durationInWeeks={durationInWeeks}
        hasCoreTag={hasCoreTag}
        iconName={iconName}
        testID={testID}
        title={title}
      />
      <VStack gap={6}>
        <Text
          testID={joinTestIDs(
            testID,
            GroupClassRegistrationModalTestID.groupCardDescription,
          )}
          variant="caption"
        >
          {description}
        </Text>
      </VStack>
    </VStack>
  </BaseCard>
);

export type { GroupRegistrationCardProps };
export default GroupRegistrationCard;
